// pages/fire/components/tableFires/TableFire.tsx
import { MoreVert as MoreVertIcon, Chat as ChatIcon } from "@mui/icons-material";
import { Box, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import React from "react";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { globalFormatCurrencyBRL, globalFormatDates, globalMaskDocument, globalTranslateCapitalizationStatus, globalBackgroundCapitalization, globalColorTextCapitalization } from "../../../shared/utils";
import { CapitalizationData } from "../../../shared/services/api/capitalization/types";
import { PermissionRolesType } from "../../../shared/types";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionRolesEnum, PermissionActionsEnum } from "../../../shared/enums/profiles";

const HEADERS = ["Criação", "Seguradora", "Imobiliária", "Inquilino", "Endereço", "Status", "Título", "Ações"] as const;

interface Props {
	rows: CapitalizationData[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: CapitalizationData, type?: "menu" | "followup") => void;
}

function renderStatusChip(status: string) {
	const label = globalTranslateCapitalizationStatus(status);
	const bg = globalBackgroundCapitalization(status);
	const color = globalColorTextCapitalization(status);
	return (
		<Chip
			size="medium"
			label={label}
			sx={{
				color,
				fontWeight: 500,
				background: bg,
				border: "0.5px solid #f1f1f1",
			}}
		/>
	);
}

export const TableCapitalization: React.FC<Props> = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, onOpenMenu }) => {
	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{
				mt: 1,
				width: "96%",
				px: { xs: 1, sm: 3 },
				py: { xs: 2, sm: 3 },
				ml: 1,
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			<Table size="small" stickyHeader aria-label="Tabela de Eventos de Fogo">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{HEADERS.map((header, i) => (
							<TableCell key={header} align={i === HEADERS.length - 1 ? "right" : "left"}>
								<Typography variant="button">{header}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.id} hover>
							<TableCell>
								<Typography>{globalFormatDates(String(row.createdAt))}</Typography>
								<Typography variant="body2" color="text.secondary">
									{row.createdAt ? format(new Date(row.createdAt as string), "HH:mm:ss", { locale: ptBR }) : ""}
								</Typography>
							</TableCell>

							<TableCell>
								<Typography>{row.insuranceCompany}</Typography>
							</TableCell>

							<TableCell>
								<HtmlTooltip
									title={row.clientName || row.realEstateId}
									arrow
									placement="top"
								>
									<Typography
										noWrap
										sx={{
											maxWidth: 200,
											cursor: "default",
											display: "inline-block",
										}}
									>
										{row.clientName || row.realEstateId}
									</Typography>
								</HtmlTooltip>
								{row.clientDocument && (
									<Typography variant="body2" color="text.secondary">
										{globalMaskDocument(row.clientDocument)}
									</Typography>
								)}
							</TableCell>

							<TableCell>
								<Typography>{row.tenantName}</Typography>
								<Typography variant="body2" color="text.secondary">
									{globalMaskDocument(row.tenantDocument)}
								</Typography>
							</TableCell>

							<TableCell>
								<HtmlTooltip
									title={`${row.propertyStreet}, ${row.propertyNumber}${row.propertyComplement ? ` - ${row.propertyComplement}` : ""}`}
									arrow
									placement="top"
								>
									<Typography
										noWrap
										sx={{
											maxWidth: 200,
											cursor: "default",
											display: "inline-block",
										}}
									>
										{`${row.propertyStreet}, ${row.propertyNumber}${row.propertyComplement ? ` - ${row.propertyComplement}` : ""}`}
									</Typography>
								</HtmlTooltip>

								<Typography variant="body2" color="text.secondary">
									{row.propertyCity} - {row.propertyState}
								</Typography>
							</TableCell>

							<TableCell>{renderStatusChip(row.status || "pendente")}</TableCell>

							<TableCell align="center">
								<Typography>{globalFormatCurrencyBRL(String(row.titleAmount))}</Typography>
							</TableCell>

							<TableCell align="right">
								{checkByProfileGuard(PermissionRolesEnum.CapitalizationOptions, PermissionActionsEnum.Update) && (
									<IconButton aria-label={`Follow-up do evento ${row.id}`} onClick={(e) => onOpenMenu(e, row, "followup")}>
										<ChatIcon />
									</IconButton>
								)}
								<IconButton aria-label={`Ações do evento ${row.id}`} onClick={(e) => onOpenMenu(e, row, "menu")}>
									<MoreVertIcon />
								</IconButton>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table >
			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer >
	);
};
